import { ref, watch, computed } from 'vue';
import { useRoute } from 'vue-router';
import { isNotOnPublicPage } from '@/utils/http';
import { getActiveSubscription, getActiveUser } from '@/data/session/session-globals';
import { hasSegmentFlag } from '@/data/flags/local-flags';

export const DEFAULT_UI_VERSION = 'current';

export const NEW_UI_VERSION = 'new';

const PENDO_INTERNAL_SUB_ID = '5668600916475904';
const uiVersion = ref(DEFAULT_UI_VERSION);

export function canShowNewUiInActiveSubscription () {
    const isProd = window?.appEnvId === 'prod';
    if (!isProd) return true;

    return getActiveSubscription()?.id === PENDO_INTERNAL_SUB_ID;
}

function getEffectiveUiVersion (version) {
    if (version === NEW_UI_VERSION && !canShowNewUiInActiveSubscription()) {
        return DEFAULT_UI_VERSION;
    }

    return version;
}

function applyUiVersion (version) {
    if (version === NEW_UI_VERSION) {
        document.documentElement.setAttribute('data-ui', NEW_UI_VERSION);
    } else {
        document.documentElement.removeAttribute('data-ui');
    }
}

function applyEffectiveUiVersion (version) {
    const effectiveVersion = getEffectiveUiVersion(version);

    uiVersion.value = effectiveVersion;
    applyUiVersion(effectiveVersion);
}

export default function useUiVersion () {
    const route = useRoute();

    const setUiVersion = (version) => {
        if (canShowNewUiInActiveSubscription()) {
            window.localStorage.setItem('pendo-ui-version', version);
            applyEffectiveUiVersion(version);
        }
    };

    const initializeUiVersion = () => {
        const setupUiVersion = () => {
            const isInNewUiSegment = getActiveUser()?.isPendozer && hasSegmentFlag('UIRefresh');
            const segmentDefault = isInNewUiSegment ? NEW_UI_VERSION : DEFAULT_UI_VERSION;
            const storedVersion = window.localStorage.getItem('pendo-ui-version');

            const normalizedVersion = () => {
                if (storedVersion && !isInNewUiSegment) {
                    return DEFAULT_UI_VERSION;
                }

                return storedVersion || segmentDefault;
            };

            applyEffectiveUiVersion(normalizedVersion());
        };

        if (isNotOnPublicPage()) {
            setupUiVersion();
        } else {
            const unwatch = watch(() => route.path, (newPath) => {
                if (newPath && isNotOnPublicPage()) {
                    unwatch();
                    setupUiVersion();
                }
            });
        }
    };

    const isNewUi = computed(() => {
        return uiVersion.value === NEW_UI_VERSION;
    });

    return {
        isNewUi,
        setUiVersion,
        initializeUiVersion,
    };
}
